import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../context/AuthContext';
import {
  getKBArticles,
  getKBArticleById,
  createKBArticle,
  updateKBArticle,
  deleteKBArticle,
} from '../services/api';
import { format } from 'date-fns';

const CATEGORIES = ['All', 'Incident', 'SAP', 'Change Management', 'General', 'How-To'];

const CATEGORY_COLORS = {
  Incident: { bg: '#fee2e2', color: '#991b1b' },
  SAP: { bg: '#e0f2fe', color: '#075985' },
  'Change Management': { bg: '#fef3c7', color: '#92400e' },
  General: { bg: '#f3f4f6', color: '#374151' },
  'How-To': { bg: '#dcfce7', color: '#166534' },
};

function ArticleModal({ article, onClose }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: 680,
          height: '100vh', overflowY: 'auto',
          background: 'var(--bg-card)',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.2)',
          animation: 'slideInRight 0.25s ease',
          padding: 32,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ flex: 1, paddingRight: 16 }}>
            {article.category && (
              <span
                className="badge"
                style={{
                  background: (CATEGORY_COLORS[article.category] || CATEGORY_COLORS.General).bg,
                  color: (CATEGORY_COLORS[article.category] || CATEGORY_COLORS.General).color,
                  marginBottom: 10, display: 'inline-block',
                }}
              >
                {article.category}
              </span>
            )}
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-main)', lineHeight: 1.35, marginBottom: 8 }}>
              {article.title}
            </h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 16 }}>
              <span>By {article.author_name || 'Unknown'}</span>
              <span>{format(new Date(article.created_at), 'dd MMM yyyy')}</span>
              <span>👁 {article.views} views</span>
            </div>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            style={{ flexShrink: 0 }}
          >
            ✕ Close
          </button>
        </div>

        <div
          style={{
            borderTop: '1px solid var(--border)',
            paddingTop: 24,
            color: 'var(--text-main)',
            fontSize: 14,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {article.content}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(60px); opacity: 0; }
          to   { transform: translateX(0);   opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function ArticleFormModal({ existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: existing?.title || '',
    content: existing?.content || '',
    category: existing?.category || 'General',
    is_published: existing?.is_published ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setError('Title and content are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (existing) {
        await updateKBArticle(existing.id, form);
      } else {
        await createKBArticle(form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save article.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: 600,
          background: 'var(--bg-card)',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          overflow: 'hidden',
          animation: 'popIn 0.2s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="card-header" style={{ padding: '20px 24px' }}>
          <span className="card-title">{existing ? 'Edit Article' : 'New Article'}</span>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          {error && (
            <div style={{
              background: 'var(--danger-bg)', color: 'var(--danger)',
              borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13,
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>
              Title *
            </label>
            <input
              className="form-input"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Article title"
              style={{
                width: '100%', padding: '9px 12px',
                background: 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text-main)', fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>
              Category
            </label>
            <select
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              style={{
                width: '100%', padding: '9px 12px',
                background: 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text-main)', fontSize: 14,
              }}
            >
              {CATEGORIES.filter(c => c !== 'All').map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>
              Content *
            </label>
            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Write the article content here..."
              rows={10}
              style={{
                width: '100%', padding: '9px 12px',
                background: 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text-main)', fontSize: 14,
                resize: 'vertical', fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <input
              type="checkbox"
              id="is_published"
              checked={form.is_published}
              onChange={e => setForm(f => ({ ...f, is_published: e.target.checked }))}
              style={{ width: 16, height: 16 }}
            />
            <label htmlFor="is_published" style={{ fontSize: 13, color: 'var(--text-sub)', cursor: 'pointer' }}>
              Publish immediately
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : existing ? 'Update Article' : 'Create Article'}
            </button>
          </div>
        </form>

        <style>{`
          @keyframes popIn {
            from { opacity: 0; transform: scale(0.96); }
            to   { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </div>
    </div>
  );
}

export default function KnowledgeBasePage() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [viewArticle, setViewArticle] = useState(null);
  const [viewFull, setViewFull] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editArticle, setEditArticle] = useState(null);

  const canWrite = user?.role === 'admin' || user?.role === 'consultant';
  const isAdmin = user?.role === 'admin';

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category !== 'All') params.category = category;
      const res = await getKBArticles(params);
      setArticles(res.data.articles || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const handleOpenArticle = async (id) => {
    try {
      const res = await getKBArticleById(id);
      setViewFull(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this article?')) return;
    try {
      await deleteKBArticle(id);
      fetchArticles();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete.');
    }
  };

  return (
    <AppLayout pageTitle="Knowledge Base">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ flex: 1, maxWidth: 420 }}>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              fontSize: 16, pointerEvents: 'none',
            }}>🔍</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search articles..."
              style={{
                width: '100%', padding: '9px 12px 9px 36px',
                background: 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 10, color: 'var(--text-main)', fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
        {canWrite && (
          <button
            className="btn btn-primary"
            onClick={() => { setEditArticle(null); setShowForm(true); }}
          >
            ✏️ New Article
          </button>
        )}
      </div>

      {/* Category Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            style={{
              padding: '6px 16px',
              borderRadius: 20,
              border: '1px solid',
              borderColor: category === cat ? 'var(--primary)' : 'var(--border)',
              background: category === cat ? 'var(--primary)' : 'var(--bg-card)',
              color: category === cat ? '#fff' : 'var(--text-sub)',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Articles Grid */}
      {loading ? (
        <div className="flex-center" style={{ height: 300 }}>
          <div className="spinner" style={{ width: 36, height: 36 }} />
        </div>
      ) : articles.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📚</div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-main)', marginBottom: 8 }}>
            No articles found
          </h3>
          <p style={{ color: 'var(--text-muted)' }}>
            {search ? 'Try a different search term or category.' : 'No articles have been published yet.'}
          </p>
          {canWrite && (
            <button
              className="btn btn-primary btn-sm"
              style={{ marginTop: 16 }}
              onClick={() => { setEditArticle(null); setShowForm(true); }}
            >
              Create first article
            </button>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 16,
        }}>
          {articles.map(article => {
            const catStyle = CATEGORY_COLORS[article.category] || CATEGORY_COLORS.General;
            const excerpt = article.excerpt
              ? article.excerpt.slice(0, 120) + (article.excerpt.length > 120 ? '…' : '')
              : '';
            return (
              <div
                key={article.id}
                className="card"
                style={{ cursor: 'pointer', transition: 'box-shadow 0.15s', padding: 20 }}
                onClick={() => handleOpenArticle(article.id)}
                onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span
                    className="badge"
                    style={{ background: catStyle.bg, color: catStyle.color }}
                  >
                    {article.category}
                  </span>
                  {canWrite && (
                    <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ padding: '3px 8px', fontSize: 11 }}
                        onClick={e => {
                          e.stopPropagation();
                          setEditArticle(article);
                          setShowForm(true);
                        }}
                      >
                        Edit
                      </button>
                      {isAdmin && (
                        <button
                          className="btn btn-sm"
                          style={{
                            padding: '3px 8px', fontSize: 11,
                            background: 'var(--danger-bg)', color: 'var(--danger)',
                            border: '1px solid var(--danger)',
                          }}
                          onClick={e => handleDelete(article.id, e)}
                        >
                          Del
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <h3 style={{
                  fontSize: 14, fontWeight: 600, color: 'var(--text-main)',
                  marginBottom: 8, lineHeight: 1.4,
                }}>
                  {article.title}
                </h3>

                <p style={{
                  fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.5,
                  marginBottom: 14,
                }}>
                  {excerpt}
                </p>

                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  fontSize: 11, color: 'var(--text-muted)',
                }}>
                  <span>{article.author_name || 'Unknown'}</span>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span>👁 {article.views}</span>
                    <span>{format(new Date(article.created_at), 'dd MMM yyyy')}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Article viewer slide-over */}
      {viewFull && (
        <ArticleModal article={viewFull} onClose={() => setViewFull(null)} />
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <ArticleFormModal
          existing={editArticle}
          onClose={() => { setShowForm(false); setEditArticle(null); }}
          onSaved={() => { setShowForm(false); setEditArticle(null); fetchArticles(); }}
        />
      )}
    </AppLayout>
  );
}
